import { Routes, Route, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { useEffect, lazy, Suspense, type ComponentType } from 'react';
import { ROUTES } from './routes';
import Navbar from "./components/Navbar";
import ParticlesBackground from "./components/ParticlesBackground";
import ScrollToTop from "./components/ScrollToTop";
import Breadcrumbs from "./components/Breadcrumbs";

// Routes chargées à la demande : en import statique, chaque page embarquait le
// code de toutes les autres (1,2 Mo de Three.js sur la home, qui n'en a pas
// besoin). `preloaded` court-circuite le lazy pour la route rendue au prerender :
// sans ça, React suspendrait à l'hydratation et jetterait le HTML prérendu.
//
// Les lazy sont construits une seule fois au niveau module. Les créer dans le
// rendu produirait un composant neuf à chaque passe, donc un remontage complet
// de la page (et la perte de tout son état) au moindre re-render du parent.
//
// Après un déploiement, les fichiers JS de l'ancienne version n'existent plus : un onglet
// resté ouvert échoue en chargeant une autre page. On recharge alors la page une fois
// (drapeau en sessionStorage : jamais de boucle) ; un second échec va à l'ErrorBoundary.
const RELOAD_FLAG = 'stale-chunk-reload';
function reloadOnStaleChunk<T>(load: () => Promise<T>) {
  return () => load().then(
    (mod) => {
      try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* stockage indisponible */ }
      return mod;
    },
    (err: unknown) => {
      try {
        if (!sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
          return new Promise<T>(() => {}); // la page se recharge : on reste en attente
        }
      } catch { /* stockage indisponible : l'erreur remonte */ }
      throw err;
    },
  );
}
const LAZY_PAGES = new Map(ROUTES.map((r) => [r.path, lazy(reloadOnStaleChunk(r.load))]));

type Props = { preloaded?: { path: string; Component: ComponentType } };

export default function AppShell({ preloaded }: Props) {
  // Clé de l'ErrorBoundary : changer de page la remonte, donc efface un écran d'erreur.
  const { pathname } = useLocation();
  // Reveal-on-scroll: handle late-mounted nodes (route changes) via MutationObserver
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const applyVisible = (el: Element) => (el as HTMLElement).classList.add('is-visible');

    const observeNode = (io: IntersectionObserver, el: Element) => {
      if (!(el instanceof HTMLElement)) return;
      if (reduce) { applyVisible(el); return; }
      io.observe(el);
    };

    // Seuil 0 et non une fraction : un bloc très haut (le calculateur fait ~3000 px)
    // n'atteignait jamais 12 % visibles sans défiler et restait invisible.
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) applyVisible(e.target);
      });
    }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });

    // Initial attach
    document.querySelectorAll('[data-reveal]').forEach((el) => observeNode(io, el));

    // Observe future additions (route swaps, lazy mounts)
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches?.('[data-reveal]')) observeNode(io, node);
          node.querySelectorAll?.('[data-reveal]').forEach((el) => observeNode(io, el));
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => { io.disconnect(); mo.disconnect(); };
  }, []);

  return (
    <>
      <ScrollToTop />
      <Breadcrumbs />
      <Navbar />
      <ParticlesBackground />
      {/* Removed global sr-only H1 to avoid duplicate H1s per page */}
      
      {/* Global CSS reset for links */}
      <style dangerouslySetInnerHTML={{
        __html: `
          a {
            text-decoration: none !important;
          }
          a:hover {
            text-decoration: none !important;
          }
          a:focus {
            text-decoration: none !important;
          }
          a:active {
            text-decoration: none !important;
          }
          a:visited {
            text-decoration: none !important;
          }
        `
      }} />
      
      <ErrorBoundary key={pathname} fallback={() => (
        <main id="main" className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" role="alert">
          <h1 className="text-3xl font-bold text-white">Something went wrong on this page</h1>
          <p className="text-slate-300">An unexpected error stopped the page from rendering.</p>
          <a className="rounded-full border border-cyan-400/50 px-5 py-2 text-cyan-200" href={typeof window !== "undefined" ? window.location.pathname : "/"}>Reload the page</a>
        </main>
      )}>
      <Suspense fallback={null}>
        <Routes>
          {ROUTES.map(({ path }) => {
            const Component =
              preloaded?.path === path ? preloaded.Component : LAZY_PAGES.get(path)!;
            return <Route key={path} path={path} element={<Component />} />;
          })}
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </>
  );
}
